// Rolling balls (visual only): a ball's mesh turns as it moves over the cloth, so the numbers and stripes of the
// Any ball set (and every other ball) roll with the motion. The physics (physics/solver.ts) has no spin; this is
// rolling without slipping worked out from how far the ball moved since the last rendered frame:
//   ω = n × v / r   (n: the cloth's normal, +y in the scene; v: the ball's velocity; r: its radius)
// so over a frame the ball turns by |d| / r about the axis n × d (d: the move on the cloth). The scene has the
// physics x on x and the physics y on z, so d = (dx, 0, dz) and n × d = (dz, 0, -dx).
// Quaternions are plain {x, y, z, w} here (no three.js), so this is unit-testable; Balls.ts copies the result into
// the mesh (mesh.useQuaternion).

export interface Quat { x: number; y: number; z: number; w: number; }

export const identityQuat = (): Quat => ({x: 0, y: 0, z: 0, w: 1});

/** a * b (b first, then a). */
export function mulQuat(a: Quat, b: Quat): Quat {
    return {
        x: a.w * b.x + a.x * b.w + a.y * b.z - a.z * b.y,
        y: a.w * b.y - a.x * b.z + a.y * b.w + a.z * b.x,
        z: a.w * b.z + a.x * b.y - a.y * b.x + a.z * b.w,
        w: a.w * b.w - a.x * b.x - a.y * b.y - a.z * b.z,
    };
}

export function normalizeQuat(q: Quat): Quat {
    const l = Math.hypot(q.x, q.y, q.z, q.w);
    if (!(l > 0) || !isFinite(l)) return identityQuat();
    return {x: q.x / l, y: q.y / l, z: q.z / l, w: q.w / l};
}

/** Rotation by `angle` about the unit axis (ax, ay, az). */
export function axisAngleQuat(ax: number, ay: number, az: number, angle: number): Quat {
    const s = Math.sin(angle / 2);
    return {x: ax * s, y: ay * s, z: az * s, w: Math.cos(angle / 2)};
}

/** Euler XYZ (three.js's default order) as a quaternion, for a mesh's starting orientation. */
export function eulerXYZQuat(x: number, y: number, z: number): Quat {
    return mulQuat(mulQuat(axisAngleQuat(1, 0, 0, x), axisAngleQuat(0, 1, 0, y)), axisAngleQuat(0, 0, 1, z));
}

/** Rotate the vector v by q. */
export function rotateVec(q: Quat, v: [number, number, number]): [number, number, number] {
    const p = mulQuat(mulQuat(q, {x: v[0], y: v[1], z: v[2], w: 0}), {x: -q.x, y: -q.y, z: -q.z, w: q.w});
    return [p.x, p.y, p.z];
}

/**
 * The orientation after the ball rolled by (dx, dz) on the cloth (scene x, z), radius r: the rolling rotation is
 * applied in world space, before the ball's current orientation. No move (or a non-finite one): unchanged.
 * Snapping a ball somewhere (a layout, ball in hand, a re-spot) doesn't come through here (Balls.ts), so it
 * doesn't roll.
 */
export function rollQuat(q: Quat, dx: number, dz: number, r: number): Quat {
    const d = Math.hypot(dx, dz);
    if (!(d > 1e-9) || !isFinite(d) || !(r > 0)) return q;
    const step = axisAngleQuat(dz / d, 0, -dx / d, d / r);
    return normalizeQuat(mulQuat(step, q));
}
